import { Link } from "react-router";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./rsa3d-sight-distance.module.css";

const PAGE = {
  title: "Sight Distance Study",
  description:
    "Michigan State University's groundbreaking study of sight distance and crash risk, and what it means for how road safety audits are done.",
  path: "/road-safety-audit-3d/sight-distance-study",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Road Safety Audit 3D", path: "/road-safety-audit-3d" },
  { name: "Sight distance study", path: PAGE.path },
];
const PDF = "/files/msu-stopping-sight-distance-lidar-study.pdf";

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/rsa3d/msu-figure1",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["finding"];
const surface = surfaceFor(LIGHT_SECTIONS);

// The study at a glance: every figure from the paper (its data section, page 5).
const GLANCE = [
  ["19", "state-maintained routes in Utah"],
  ["274 mi", "of high-fidelity LiDAR"],
  ["80 mph", "freeways, and non-freeways posted 30 to 65 mph"],
  ["2", "sets of models: freeways and non-freeways, estimated separately"],
];

// "Safety Evaluation of Available Stopping Sight Distance using High-Fidelity LiDAR Data",
// Peter T. Savolainen, Megat-Usamah Megat-Johari, Nischal Gupta and Timothy J. Gates, Michigan
// State University. Abstract and conclusion quoted as published; attribution paragraph from RDV.
const STUDY = [
  {
    heading: "The question",
    paragraphs: [
      "Researchers Peter T. Savolainen, Ph.D., P.E., Megat-Usamah Megat-Johari, Ph.D., Nischal Gupta, M.Sc., and Timothy J. Gates, Ph.D., P.E. set out to create a new pathway to help roadway engineers build and redesign safer roads, releasing a sight distance study.",
      "Stopping sight distance (SSD) has long been cited as a concern among design engineers, given difficulties in attaining minimum values in certain roadway contexts in consideration of cost constraints. For example, SSD is one of the ten controlling criteria for design and documentation of design exceptions. However, the extant research literature has generally not shown that locations with insufficient SSD experience higher crash risks as compared to locations that meet or exceed recommended design values.",
    ],
  },
  {
    heading: "How the study was done",
    paragraphs: [
      "This study investigates the relationships between SSD and crash risk using high-fidelity LiDAR data. These data are used to examine crash trends on the roadway network maintained by the Utah Department of Transportation. LiDAR data are used to assess compliance with the current SSD design policy. These data are integrated with crash and roadway geometric information.",
      "A series of negative binomial regression models are estimated to assess the relationship between available sight distance and the frequency of crashes while controlling for other important variables of interest. Negative binomial regression models were estimated separately for freeway and non-freeway facilities while controlling for the effects of other important variables.",
      "RDV's Road Safety Audit 3D technology was the tool used in this study to match the LiDAR data to the 3D environment.",
    ],
  },
  {
    heading: "What it found",
    paragraphs: [
      "The results show that roadways with limited sight distance tended to experience significantly more crashes as compared to other, similar segments where sight restrictions were not present.",
      "The results show similar relationships for both facility types, with crashes persistently increasing as the amount of available sight distance is reduced. The study demonstrated the value of high-fidelity LiDAR data, which allowed for a large-scale investigation of the relationship between crash risk and available sight distance on a diverse set of roadway facilities.",
    ],
  },
  {
    heading: "What it means for audits",
    paragraphs: [
      "Stopping sight distance is one of the most important criteria for highway design. However, the literature has shown limited quantitative evidence as to the nature of this relationship. As such, the results from this study provide an important contribution to the research literature.",
      "The safety performance functions estimated as a part of this study provide an empirical basis for estimating the potential impacts of design scenarios where it may be impractical to satisfy the minimum recommendation distances from the AASHTO Green Book. The results can also help to inform agency practices and the development of projects to mitigate the sources of the sight distance limitations.",
      "For the first time, this predictive data enables engineers to have precise data to show where deficiencies are in the roadway. Road safety audits no longer need to rely on roadway deaths.",
    ],
  },
];

export default function SightDistanceStudy() {
  return (
    <>
      <section className={styles.hero} aria-labelledby="study-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="study-title" className={styles.heroTitle}>
              Less sight distance, more crashes
            </h1>
            <p className={styles.heroLead}>
              Michigan State University researchers used LiDAR of 274 miles of Utah highways,
              matched to the 3D road by RDV&apos;s Road Safety Audit 3D, to measure how available
              stopping sight distance relates to crashes.
            </p>
            <div className={styles.heroActions}>
              <a className="btn btn-primary" href={PDF} download>
                Download the study (PDF)
              </a>
              <Link to="/road-safety-audit-3d" className={styles.heroLink}>
                Road Safety Audit 3D
              </Link>
            </div>
            <p className={styles.citation}>
              <cite>
                Safety Evaluation of Available Stopping Sight Distance using High-Fidelity LiDAR
                Data
              </cite>
              . Megat-Usamah Megat-Johari, Nischal Gupta, Peter T. Savolainen and Timothy J. Gates,
              Department of Civil and Environmental Engineering, Michigan State University. January
              4, 2023.
            </p>
          </div>
          <figure className={styles.heroFigure}>
            <Picture
              src="/images/site/rsa3d/msu-figure1"
              alt="An aerial view of a curving mountain road with sight lines drawn from each point on it: green and yellow where the view is clear, red where it is restricted"
              ratio="1871 / 1274"
              widths={[480, 960, 1600]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 50vw, 100vw"
              priority
              className={styles.heroImage}
            />
            <figcaption className={styles.figcaption}>
              Figure 1 from the study: available sight distance on a Utah route, with sight
              restrictions in red.
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section" aria-label="The study at a glance">
        <div className="container">
          <ul className={styles.glance}>
            {GLANCE.map(([value, label]) => (
              <li key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* The result, in the paper's words and its own chart. */}
      <section
        className="section"
        data-surface={surface("finding")}
        aria-labelledby="finding-title"
      >
        <div className={`container ${styles.finding}`}>
          <div>
            <h2 id="finding-title" className="sr-only">
              The finding
            </h2>
            <blockquote className={styles.quote}>
              <p>
                &ldquo;Roadways with limited sight distance tended to experience significantly more
                crashes as compared to other, similar segments where sight restrictions were not
                present.&rdquo;
              </p>
              <footer>The study&apos;s results</footer>
            </blockquote>
          </div>
          <figure className={styles.chart}>
            <Picture
              src="/images/site/rsa3d/msu-figure7"
              alt="A chart of crashes per mile per year on non-freeways against available stopping sight distance: about 1.5 crashes at 250 feet, falling steadily to about 0.75 at 1,050 feet, for all three ways of specifying sight distance"
              ratio="1913 / 1060"
              widths={[960, 1440, 1904]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 55vw, 100vw"
              className={styles.chartImage}
            />
            <figcaption className={styles.figcaption}>
              Figure 7 from the study: estimated crashes per mile per year on non-freeways, as
              available stopping sight distance grows.
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section" aria-labelledby="paper-title">
        <div className="container">
          <SectionHeader
            id="paper-title"
            title="The study, in its own words"
            aside="Abstract and conclusions as published, with a note from RDV on the tool used."
            link={{
              to: "/blog/line-of-sight-vs-sight-distance",
              label: "Line of sight vs. sight distance",
            }}
          />
          <ol className={styles.parts}>
            {STUDY.map((part) => (
              <li key={part.heading}>
                <h3>{part.heading}</h3>
                <div className={styles.partBody}>
                  {part.paragraphs.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                </div>
              </li>
            ))}
          </ol>
          <p className={styles.download}>
            <a className="btn btn-secondary" href={PDF} download>
              Download the full study (PDF)
            </a>
          </p>
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/site/rsa3d/existing-roads"
        title="See your roads the way the study did"
        text="Road Safety Audit 3D applies the same sight-distance analysis to your design or scan."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
